import { Component, ChangeDetectionStrategy, ViewChild, TemplateRef, Input, SimpleChanges, Injector, OnInit, AfterViewInit } from '@angular/core';
import { CalendarEvent, CalendarEventAction, CalendarEventTimesChangedEvent, CalendarView } from 'angular-calendar';
import { isSameDay, isSameMonth } from 'date-fns';
import { Subject } from 'rxjs';
import { modalDataI } from 'app/core/interfaces/schedule';
import { cardColors } from 'app/core/data/calendar.json';
import * as moment from 'moment';
import { Encounter } from 'app/core/interfaces/encounter';
import { CalendarDayOptionsComponent } from '../../components/calendar-day-options/calendar-day-options.component';
import { BaseComponent } from 'app/modules/shared/components/base.component';
import { MatDialogConfig } from '@angular/material/dialog';
import { OrganizationService } from 'app/core/http/services/organization.service';

@Component({
  selector: 'page-scheduler',
  templateUrl: './scheduler.component.html',
  styleUrls: ['./scheduler.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SchedulerComponent extends BaseComponent implements OnInit, AfterViewInit {
  @ViewChild('modalContent', { static: true }) modalContent: TemplateRef<any> = <any>{};
  @ViewChild('customMonthCell', { static: true }) customMonthCell!: TemplateRef<any>;
  @Input() encounters: any[] = [];

  holdTimeout: any;
  heldDate: Date | null = null;
  view: CalendarView = CalendarView.Month;
  CalendarView = CalendarView;
  viewDate: Date = new Date();
  modalData: modalDataI = <modalDataI>{};
  refresh = new Subject<void>();
  events: any[] = [];
  activeDayIsOpen: boolean = true;
  public mobile = false;

  actions: CalendarEventAction[] = [
    {
      label: '<mat-icon color="primary">edit</mat-icon>',
      a11yLabel: 'Editar',
      onClick: ({ event }: { event: CalendarEvent }): void => {
        this.handleEvent('Edited', event);
      },
    },
    {
      label: '<mat-icon color="primary">trash</mat-icon>',
      a11yLabel: 'Remover',
      onClick: ({ event }: { event: CalendarEvent }): void => {
        this.events = this.events.filter((iEvent) => iEvent !== event);
        this.handleEvent('Deleted', event);
      },
    },
  ];

  public organization: any;
  public userLogged = {
    name: '',
    id: '',
  };

  constructor(private injector: Injector, private organizationService: OrganizationService) {
    super(injector);
  }

  ngOnInit() {
    const user = this.currentUser.user;
    this.userLogged = {
      name: user,
      id: user.id,
    };
    this.getOrganization(this.userLogged.id);
    this.events = this.addEvent(this.encounters);
    this.mobile = window.innerWidth < 768;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['encounters']) {
      const newEncounters = changes['encounters'].currentValue;
      this.events = this.addEvent(newEncounters);
    }
  }

  ngAfterViewInit(): void {
    this.setHeader();
  }

  getOrganization(organization_id: string) {
    this.organizationService.showNew(organization_id).subscribe({
      next: (organization: any) => {
        this.organization = organization;
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  getLabelByType(type: string) {
    if (window.innerWidth <= 430) {
      return 'x';
    }
    return type === 'holiday' ? 'Feriado' : 'Bloqueado';
  }

  setHeader() {
    if (window.innerWidth < 768) {
      setTimeout(() => {
        document
          .querySelectorAll('.cal-header .cal-cell')
          .forEach((item: Element) => (item.innerHTML = (item as HTMLElement).innerText.substring(0, 3).toUpperCase()));
      }, 300);
    }
  }

  getModifiedDayType(date: Date): 'holiday' | 'blockSchedule' | null {
    if (!this.organization?.modifiedDays) return null;

    const target = moment(date).format('YYYY-MM-DD');
    const match = this.organization.modifiedDays.find((item: any) => {
      return moment(item.date).format('YYYY-MM-DD') === target;
    });

    return match?.type || null;
  }

  timeFormat(date: any) {
    if (!moment.isMoment(date)) {
      date = moment(date);
    }
    return date.format('HH:mm');
  }

  dayClicked({ date, events }: { date: Date; events: CalendarEvent[] }): void {
    if (isSameMonth(date, this.viewDate)) {
      if ((isSameDay(this.viewDate, date) && this.activeDayIsOpen === true) || events.length === 0) {
        this.activeDayIsOpen = false;
      } else {
        this.activeDayIsOpen = true;
      }
      this.viewDate = date;
    }
  }

  eventTimesChanged({ event, newStart, newEnd }: CalendarEventTimesChangedEvent): void {
    this.events = this.events.map((iEvent) => {
      if (iEvent === event) {
        return {
          ...event,
          start: newStart,
          end: newEnd,
        };
      }
      return iEvent;
    });
    this.handleEvent('Dropped or resized', event);
  }

  handleEvent(action: string, event: CalendarEvent): void {
    // this.modalData = { event, action };
    // this.modal.open(this.modalContent, { size: 'lg' });
  }

  addEvent(encounter: Encounter[]) {
    const events = encounter.map((item, index) => {
      let event = {};
      if (item.type === 'Consulta') {
        event = {
          start: moment(item.startDate).toDate(),
          end: moment(item.startDate).add(30, 'minute').toDate(),
          title: `<div class="left event-content">
              <b>${item.type}</b>
              <br> <b>Especialista:</b> ${item.practitioner.name}
              <br> <b>Procedimento:</b> ${item.name}
              <br> <b>Paciente:</b> ${item.patient.name}
              <br> <div class="event-hour"><b>Hora:</b> ${this.timeFormat(item.startDate)}</div>
            </div>`,
          color: { ...cardColors['blue'] },
          allDay: false,
          resizable: {
            beforeStart: true,
            afterEnd: true,
          },
        };
      } else if (item.type === 'Exame') {
        event = {
          start: moment(item.startDate).toDate(),
          end: moment(item.startDate).add(30, 'minute').toDate(),
          title: `<div class="left event-content">
              <b>${item.type}</b>
              <br> <b>Especialista:</b> ${item.practitioner.name}
              <br> <b>Procedimento:</b> ${item.name}
              <br> <b>Paciente:</b> ${item.patient.name}
              <br> <div class="event-hour"><b>Hora:</b> ${this.timeFormat(item.startDate)}</div>
            </div>`,
          color: { ...cardColors['yellow'] },
          actions: undefined,
          resizable: {
            beforeStart: true,
            afterEnd: true,
          },
        };
      }
      return event;
    });

    return events;
  }

  deleteEvent(eventToDelete: CalendarEvent) {
    this.events = this.events.filter((event) => event !== eventToDelete);
  }

  setView(view: CalendarView) {
    this.view = view;
    this.setHeader();
  }

  closeOpenMonthViewDay() {
    this.activeDayIsOpen = false;
    this.setHeader();
  }

  onDayMouseDown(date: Date) {
    this.holdTimeout = setTimeout(() => {
      this.openDayOptionsModal(date);
    }, 1000);
  }

  onDayMouseUp() {
    clearTimeout(this.holdTimeout);
  }

  openDayOptionsModal(date: Date) {
    const formattedDate = moment(date).format('YYYY-MM-DDTHH:mm:ssZ');
    const alreadyModified = this.organization?.modifiedDays?.find((item: any) => item.date.startsWith(formattedDate.split('T')[0]));

    const data = {
      param: {
        date: formattedDate,
        organization: this.organization,
        existingModification: alreadyModified ?? null,
      },
    };

    const config: MatDialogConfig = {
      width: '400px',
      maxWidth: '90%',
      minHeight: '200px',
    };

    const dialogRef = this.dialog.openComponentDialog(CalendarDayOptionsComponent, data, config).subscribe((res: any) => {
      if (res) {
        this.getOrganization(this.userLogged.id);
      }
    });
  }
}
